import { create } from 'zustand';
import { getOffers } from '../api/generated/offers/offers';
import {
  getOfferPositions,
} from '../api/generated/offer-positions/offer-positions';
import { getCustomers } from '../api/generated/customers/customers';
import { getObjects } from '../api/generated/objects/objects';
import { useToastStore } from './useToastStore';
import { parseAxiosError, showApiError, translateApiError } from '../errors';
import { downloadOfferPdf } from '@/utils/downloadReport';
import { DEFAULT_PAGE_SIZE } from '@/constants/pagination';
import { i18n } from '../i18n';
import type {
  CreateOfferRequest, OfferListDto, OfferPositionFromTaskItem, OfferState,
  UpdateOfferPositionRequest, UpdateOfferRequest,
} from './offerStoreTypes';
import type { GetApiV1OffersParams } from '../api/generated/models';

export type { OfferPositionFromTaskItem } from './offerStoreTypes';

// Get API functions from factories
const {
  getApiV1Offers,
  postApiV1Offers,
  getApiV1OffersId,
  putApiV1OffersId,
  deleteApiV1OffersId,
  putApiV1OffersIdStatus,
  postApiV1OffersIdDuplicate,
} = getOffers();

const {
  putApiV1OffersOfferIdPositionsPositionId,
  deleteApiV1OffersOfferIdPositionsPositionId,
  postApiV1OffersOfferIdPositionsFromTasks,
  putApiV1OffersOfferIdPositionsReorder,
} = getOfferPositions();

const { getApiV1Customers } = getCustomers();
const { getApiV1Objects } = getObjects();

const NO_OFFERS: OfferListDto[] = [];

/** Selector for one object's offers; stable empty array until they are loaded. */
export const selectObjectOffers = (objectId: string | undefined) =>
  (state: OfferState): OfferListDto[] => (objectId && state.objectOffers[objectId]) || NO_OFFERS;

/**
 * Runs one position mutation, reloads the offer (totals + billing months change server-side)
 * and toasts the translated result. Errors go through `showApiError`, so backend codes such as
 * `offers.lockedByInvoice` are localized.
 */
async function runPositionAction(
  set: (partial: Partial<OfferState>) => void,
  offerId: string,
  successKey: string,
  call: () => Promise<unknown>,
  params?: Record<string, unknown>,
): Promise<void> {
  set({ saving: true, error: null });
  try {
    await call();
    const offer = await getApiV1OffersId(offerId);
    set({ selectedOffer: offer, saving: false });
    useToastStore.getState().addToast(i18n.t(successKey, params) as string, 'success');
  } catch (error) {
    set({ error: 'offers:positions.messages.updateError', saving: false });
    showApiError(error);
    throw error;
  }
}

export const useOfferStore = create<OfferState>((set, get) => ({
  offers: [],
  lastQuery: undefined,
  objectOffers: {},
  selectedOffer: null,
  loading: false,
  saving: false,
  downloadingPdf: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  customerNames: {},
  objectNames: {},

  fetchOffers: async (params?: GetApiV1OffersParams) => {
    set({ loading: true, error: null, lastQuery: params });
    try {
      const response = await getApiV1Offers(params);
      set({
        offers: response.items || [],
        totalCount: response.totalCount || 0,
        page: response.page || 1,
        pageSize: response.pageSize || DEFAULT_PAGE_SIZE,
        loading: false,
      });
    } catch (error) {
      const msg = error instanceof Error
        ? error.message
        : 'Fehler beim Laden der Angebote';
      set({ error: msg, loading: false });
    }
  },

  fetchObjectOffers: async (objectId: string) => {
    try {
      // The generated params type lacks objectId; the endpoint accepts it.
      const response = await getApiV1Offers({ objectId, pageSize: 50 } as GetApiV1OffersParams);
      set((state) => ({ objectOffers: { ...state.objectOffers, [objectId]: response.items || [] } }));
    } catch (error) {
      set({ error: translateApiError(parseAxiosError(error)) });
    }
  },

  getOfferById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const offer = await getApiV1OffersId(id);
      set({ selectedOffer: offer, loading: false });
      return offer;
    } catch (error) {
      const msg = error instanceof Error
        ? error.message
        : 'Fehler beim Laden des Angebots';
      set({
        error: msg,
        loading: false,
        selectedOffer: null,
      });
      return null;
    }
  },

  createOffer: async (data: CreateOfferRequest) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1Offers(data);
      await get().fetchOffers(get().lastQuery);
      set({ saving: false });
      useToastStore.getState().addToast(
        'Angebot erfolgreich erstellt', 'success'
      );
      return response.id ?? null;
    } catch (error) {
      // showApiError translates via the new error contract and skips the toast
      // when per-field errors are present (form handles those inline).
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  createOfferFromOffer: async (id: string) => {
    set({ saving: true, error: null });
    try {
      const response = await postApiV1OffersIdDuplicate(id);
      await get().fetchOffers(get().lastQuery);
      set({ saving: false });
      useToastStore.getState().addToast(
        'Neues Angebot aus dem bestehenden erstellt', 'success'
      );
      return response.id ?? null;
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  updateOffer: async (
    id: string, data: UpdateOfferRequest
  ) => {
    set({ saving: true, error: null });
    try {
      await putApiV1OffersId(id, data);
      await get().fetchOffers(get().lastQuery);
      set({ saving: false });
      useToastStore.getState().addToast(
        'Angebot erfolgreich aktualisiert', 'success'
      );
    } catch (error) {
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  deleteOffer: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await deleteApiV1OffersId(id);
      set((state) => ({
        offers: state.offers.filter((o) => o.id !== id),
        totalCount: state.totalCount - 1,
        saving: false,
      }));
      useToastStore.getState().addToast(
        'Angebot erfolgreich gelöscht', 'success'
      );
    } catch (error) {
      const msg = error instanceof Error
        ? error.message
        : 'Fehler beim Löschen des Angebots';
      set({ error: msg, saving: false });
      await get().fetchOffers(get().lastQuery);
      useToastStore.getState().addToast(msg, 'error');
      throw error;
    }
  },

  changeOfferStatus: async (id: string, status: string) => {
    set({ saving: true, error: null });
    try {
      await putApiV1OffersIdStatus(id, { status });
      const offer = await getApiV1OffersId(id);
      set({ selectedOffer: offer, saving: false });
      // For 'accepted', the caller emits a richer toast (auto-draft hint + action link).
      if (status !== 'accepted') {
        useToastStore.getState().addToast(
          'Status erfolgreich geändert', 'success'
        );
      }
    } catch (error) {
      const msg = error instanceof Error
        ? error.message
        : 'Fehler beim Ändern des Status';
      set({ error: msg, saving: false });
      useToastStore.getState().addToast(msg, 'error');
      throw error;
    }
  },

  updatePosition: async (
    offerId: string,
    positionId: string,
    data: UpdateOfferPositionRequest
  ) => {
    await runPositionAction(set, offerId, 'offers:positions.messages.updated',
      () => putApiV1OffersOfferIdPositionsPositionId(offerId, positionId, data));
  },

  deletePosition: async (
    offerId: string, positionId: string
  ) => {
    await runPositionAction(set, offerId, 'offers:positions.messages.deleted',
      () => deleteApiV1OffersOfferIdPositionsPositionId(offerId, positionId));
  },

  addPositionsFromTasks: async (
    offerId: string, items: OfferPositionFromTaskItem[]
  ) => {
    await runPositionAction(set, offerId, 'offers:positions.messages.importedFromTasks',
      () => postApiV1OffersOfferIdPositionsFromTasks(offerId, { items }),
      { count: items.length });
  },

  reorderPositions: async (
    offerId: string, positionIds: string[]
  ) => {
    set({ saving: true, error: null });
    try {
      await putApiV1OffersOfferIdPositionsReorder(
        offerId, { positionIds }
      );
      const offer = await getApiV1OffersId(offerId);
      set({ selectedOffer: offer, saving: false });
    } catch (error) {
      const msg = error instanceof Error
        ? error.message
        : 'Fehler beim Sortieren der Positionen';
      set({ error: msg, saving: false });
      useToastStore.getState().addToast(msg, 'error');
      throw error;
    }
  },

  downloadPdf: async (offerId: string) => {
    set({ downloadingPdf: true });
    try {
      await downloadOfferPdf(offerId);
      useToastStore.getState().addToast(
        'offers:messages.pdfDownloadSuccess', 'success'
      );
    } catch (error) {
      const msg = error instanceof Error
        ? error.message
        : 'offers:messages.pdfDownloadError';
      useToastStore.getState().addToast(msg, 'error');
    } finally {
      set({ downloadingPdf: false });
    }
  },

  loadNameLookups: async () => {
    try {
      const customerNames: Record<string, string> = {};
      const objectNames: Record<string, string> = {};

      const loadAllCustomers = async () => {
        let currentPage = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await getApiV1Customers({
            pageSize: 200, page: currentPage,
          });
          for (const c of res.items || []) {
            if (c.id && c.name) customerNames[c.id] = c.name;
          }
          const total = res.totalCount ?? 0;
          hasMore = currentPage * 200 < total;
          currentPage++;
        }
      };

      const loadAllObjects = async () => {
        let currentPage = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await getApiV1Objects({
            pageSize: 200, page: currentPage,
          });
          for (const o of res.items || []) {
            if (o.id && o.name) objectNames[o.id] = o.name;
          }
          const total = res.totalCount ?? 0;
          hasMore = currentPage * 200 < total;
          currentPage++;
        }
      };

      await Promise.all([loadAllCustomers(), loadAllObjects()]);
      set({ customerNames, objectNames });
    } catch {
      useToastStore.getState().addToast(
        'Fehler beim Laden der Zuordnungen', 'error'
      );
    }
  },
}));
